/**
 * Shim de transport RDP — pont entre IronRDP-web (WASM) et la WebSocket
 * relais multiplexée de la console.
 *
 * ── Le problème ──────────────────────────────────────────────────────────────
 * IronRDP-web (`ironrdp-wasm`) n'expose AUCUN mécanisme de transport custom :
 * son `SessionBuilder.proxyAddress(url)` provoque l'ouverture, côté Rust/WASM,
 * d'une `new WebSocket(url)` *directe* (via le crate `gloo-net`). Il n'est pas
 * possible de lui passer un objet duplex.
 *
 * ── La solution ──────────────────────────────────────────────────────────────
 * On installe, le temps de l'établissement de session, une classe `WebSocket`
 * de substitution sur `globalThis.WebSocket`. Quand le code WASM fait
 * `new WebSocket("rdp://tunnel")`, il obtient en réalité une instance de
 * `RdpTunnelSocket` qui :
 *   - présente exactement la surface d'API d'une WebSocket standard
 *     (`readyState`, `binaryType`, `send`, `close`, handlers `onopen`/
 *     `onmessage`/`onclose`/`onerror`, `addEventListener`) ;
 *   - en `send(bytes)` → construit une trame WebSocket BINAIRE
 *     `[0x52,0x44] + tunnelId(16o) + payload` et la pousse telle quelle sur la
 *     WS relais (pas de Base64, pas de JSON par chunk) ;
 *   - reçoit les trames binaires `rdp:data` entrantes (routées par le panneau
 *     via `feed()`) et les délivre au WASM comme des `MessageEvent` binaires.
 *
 * Le multiplexage par `tunnelId` permet à plusieurs tunnels (ou au flux vidéo)
 * de cohabiter sur l'unique WebSocket relais. Le format binaire reflète celui
 * de l'agent C# (`RdpTunnelClient.cs`).
 *
 * ── Format de trame binaire `rdp:data` ───────────────────────────────────────
 *   [0x52, 0x44]            magic "RD" (2 octets)
 *   [tunnelId : 16 octets]  le GUID du tunnel en octets bruts
 *   [payload]               octets RDP TCP bruts, tels quels
 *
 * ── Discipline binaire ───────────────────────────────────────────────────────
 * Les octets RDP ne transitent JAMAIS en clair dans les logs : seules des
 * longueurs sont éventuellement journalisées.
 */

/**
 * Émetteur minimal vers la WS relais — implémenté par le panneau (ws.send).
 * Le `type` est obligatoire : c'est le discriminant des messages relais
 * (`rdp:open`, `rdp:close`…), compatible avec l'union `WsOutgoing`.
 * N'est plus utilisé que pour les messages de CONTRÔLE (JSON).
 */
export type RelaySend = (msg: { type: string; [key: string]: unknown }) => void;

/**
 * Émetteur de trames WebSocket BINAIRES vers la WS relais — implémenté par le
 * panneau (ws.sendBinary). Utilisé pour le flux de données RDP haut débit.
 */
export type RelaySendBinary = (frame: ArrayBuffer) => void;

/** En-tête de trame binaire : magic "RD" (2 octets) + GUID (16 octets). */
const FRAME_HEADER_SIZE = 2 + 16;
const FRAME_MAGIC0 = 0x52; // 'R'
const FRAME_MAGIC1 = 0x44; // 'D'

/**
 * Convertit un GUID textuel ("xxxxxxxx-xxxx-...") en 16 octets bruts, dans le
 * même ordre que `System.Guid.ToByteArray()` côté .NET (mixed-endian : les 3
 * premiers groupes en little-endian, les 2 derniers en big-endian).
 */
function guidToBytes(guid: string): Uint8Array {
  const hex = guid.replace(/-/g, "");
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) throw new Error(`tunnelId non-GUID: ${guid}`);
  const b = new Uint8Array(16);
  for (let i = 0; i < 16; i++) b[i] = parseInt(hex.substr(i * 2, 2), 16);
  // Réordonnancement pour matcher Guid.ToByteArray() (Data1/2/3 little-endian).
  const swap = (i: number, j: number) => {
    const t = b[i];
    b[i] = b[j];
    b[j] = t;
  };
  swap(0, 3);
  swap(1, 2);
  swap(4, 5);
  swap(6, 7);
  return b;
}

/**
 * Encode une trame binaire `rdp:data` stable :
 * `[0x52,0x44] + tunnelId(16o Guid.ToByteArray) + payload`.
 */
export function encodeRdpFrame(tunnelId: string, payload: ArrayBuffer | ArrayBufferView): ArrayBuffer {
  const payloadView =
    payload instanceof ArrayBuffer
      ? new Uint8Array(payload)
      : new Uint8Array(payload.buffer, payload.byteOffset, payload.byteLength);
  const frame = new Uint8Array(FRAME_HEADER_SIZE + payloadView.length);
  frame[0] = FRAME_MAGIC0;
  frame[1] = FRAME_MAGIC1;
  frame.set(guidToBytes(tunnelId), 2);
  frame.set(payloadView, FRAME_HEADER_SIZE);
  return frame.buffer;
}

/**
 * Fausse WebSocket multiplexée sur le canal `rdp:*` de la WS relais.
 * Implémente la surface d'API que `gloo-net` (donc IronRDP-web) utilise.
 */
export class RdpTunnelSocket implements Partial<WebSocket> {
  // ── Constantes d'état WebSocket ────────────────────────────────────────────
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSING = 2;
  static readonly CLOSED = 3;
  readonly CONNECTING = 0;
  readonly OPEN = 1;
  readonly CLOSING = 2;
  readonly CLOSED = 3;

  // ── Surface WebSocket ──────────────────────────────────────────────────────
  url = "";
  readyState: number = RdpTunnelSocket.CONNECTING;
  binaryType: BinaryType = "arraybuffer";
  bufferedAmount = 0;
  extensions = "";
  protocol = "";

  onopen: ((ev: Event) => void) | null = null;
  onmessage: ((ev: MessageEvent) => void) | null = null;
  onclose: ((ev: CloseEvent) => void) | null = null;
  onerror: ((ev: Event) => void) | null = null;

  private listeners = new Map<string, Set<EventListenerOrEventListenerObject>>();

  constructor(
    private readonly tunnelId: string,
    private readonly relaySend: RelaySend,
    private readonly relaySendBinary: RelaySendBinary,
  ) {}

  // ── Cycle de vie piloté par le panneau ─────────────────────────────────────

  /**
   * Marque le tunnel comme ouvert et notifie le WASM. Appelé par le panneau
   * une fois `rdp:open` envoyé (le tunnel relais↔agent est alors prêt).
   */
  markOpen(): void {
    if (this.readyState !== RdpTunnelSocket.CONNECTING) return;
    this.readyState = RdpTunnelSocket.OPEN;
    this.dispatch("open", new Event("open"));
  }

  /**
   * Délivre au WASM des octets RDP reçus de l'agent. Le panneau extrait le
   * payload de la trame binaire `rdp:data` et appelle cette méthode.
   */
  feed(payload: ArrayBuffer): void {
    if (this.readyState !== RdpTunnelSocket.OPEN) return;
    // gloo-net lit `event.data` ; en `binaryType: "arraybuffer"` il attend un ArrayBuffer.
    const ev = new MessageEvent("message", { data: payload });
    this.dispatch("message", ev);
  }

  /** Ferme le tunnel côté WASM (échec agent ou démontage). */
  failOrClose(code: number, reason: string): void {
    if (this.readyState === RdpTunnelSocket.CLOSED) return;
    this.readyState = RdpTunnelSocket.CLOSED;
    if (code !== 1000) {
      this.dispatch("error", new Event("error"));
    }
    this.dispatch("close", new CloseEvent("close", { code, reason, wasClean: code === 1000 }));
  }

  // ── API WebSocket appelée par le WASM IronRDP ──────────────────────────────

  /**
   * Le WASM envoie des octets RDP → on construit la trame binaire
   * `[0x52,0x44] + tunnelId(16o) + payload` et on l'émet telle quelle.
   */
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    // Diagnostic : log chaque appel WASM→shim avec l'état et la taille.
    // Le panneau a installé une capture console.* dans l'overlay debug.
    if (this.readyState !== RdpTunnelSocket.OPEN) {
      console.warn(`[rdp-shim] send ignoré — readyState=${this.readyState} (attendu OPEN=1)`);
      return;
    }

    let payload: Uint8Array | null = null;
    if (typeof data === "string") {
      // IronRDP ne devrait émettre que du binaire ; couverture défensive.
      payload = new TextEncoder().encode(data);
    } else if (data instanceof ArrayBuffer) {
      payload = new Uint8Array(data);
    } else if (ArrayBuffer.isView(data)) {
      payload = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    } else if (typeof Blob !== "undefined" && data instanceof Blob) {
      // gloo-net / web-sys peut envoyer un Blob (binding `WebSocket.send`
      // accepte aussi Blob). Conversion asynchrone — l'ordre RDP est
      // préservé tant que Blob.arrayBuffer() se résout dans l'ordre d'appel
      // (microtâches FIFO). Émission via cette même méthode pour reprendre
      // le branchement ArrayBuffer ci-dessus.
      data
        .arrayBuffer()
        .then((ab) => this.send(ab))
        .catch((e) =>
          console.error(`[rdp-shim] blob.arrayBuffer échec : ${e instanceof Error ? e.message : String(e)}`),
        );
      return;
    } else {
      const ctor = (data as { constructor?: { name?: string } } | null)?.constructor?.name;
      console.warn(`[rdp-shim] send ignoré — type non supporté (${ctor ?? typeof data})`);
      return;
    }

    // Trame : en-tête (magic + GUID) + payload RDP brut.
    const frame = encodeRdpFrame(this.tunnelId, payload);
    try {
      this.relaySendBinary(frame);
      // Log léger sur les premières frames + tous les ~1 Mo.
      this.txFrames = (this.txFrames ?? 0) + 1;
      this.txBytes = (this.txBytes ?? 0) + payload.length;
      if (this.txFrames <= 3 || this.txFrames % 200 === 0)
        console.debug(`[rdp-shim] send #${this.txFrames} ${payload.length} o (cumul ${this.txBytes} o)`);
    } catch (e) {
      console.error(`[rdp-shim] relaySendBinary a levé : ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  /** Compteurs internes pour les logs de debug. */
  private txFrames?: number;
  private txBytes?: number;

  /** Le WASM ferme la connexion (fin de session RDP, shutdown). */
  close(code = 1000, reason = ""): void {
    if (this.readyState === RdpTunnelSocket.CLOSED || this.readyState === RdpTunnelSocket.CLOSING) {
      return;
    }
    this.readyState = RdpTunnelSocket.CLOSING;
    const closeReason = reason || `wasm-close:${code}`;
    // Prévenir le relais/agent que le tunnel doit être fermé (contrôle JSON).
    this.relaySend({ type: "rdp:close", tunnelId: this.tunnelId, reason: closeReason });
    this.readyState = RdpTunnelSocket.CLOSED;
    this.dispatch("close", new CloseEvent("close", { code, reason: closeReason, wasClean: true }));
  }

  // ── EventTarget minimal (gloo-net branche via addEventListener) ────────────

  addEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener);
  }

  removeEventListener(type: string, listener: EventListenerOrEventListenerObject): void {
    this.listeners.get(type)?.delete(listener);
  }

  dispatchEvent(ev: Event): boolean {
    this.dispatch(ev.type, ev);
    return true;
  }

  /** Notifie à la fois le handler `on<type>` et les `addEventListener`. */
  private dispatch(type: string, ev: Event): void {
    const handler = (this as unknown as Record<string, unknown>)[`on${type}`];
    if (typeof handler === "function") {
      try {
        (handler as (e: Event) => void).call(this, ev);
      } catch (err) {
        console.error(`[rdp-transport] handler on${type}:`, err);
      }
    }
    const set = this.listeners.get(type);
    if (set) {
      for (const l of set) {
        try {
          if (typeof l === "function") l.call(this, ev);
          else l.handleEvent(ev);
        } catch (err) {
          console.error(`[rdp-transport] listener ${type}:`, err);
        }
      }
    }
  }
}

/**
 * Décode une trame binaire `rdp:data` reçue de la WS relais.
 * Retourne `null` si la trame n'est pas une trame RDP (mauvais magic / trop
 * courte). Sinon retourne le `tunnelId` (GUID textuel) et le `payload`.
 */
export function parseRdpFrame(buf: ArrayBuffer): { tunnelId: string; payload: ArrayBuffer } | null {
  if (buf.byteLength < FRAME_HEADER_SIZE) return null;
  const view = new Uint8Array(buf);
  if (view[0] !== FRAME_MAGIC0 || view[1] !== FRAME_MAGIC1) return null;

  // tunnelId : 16 octets bruts → GUID textuel (ordre Guid.ToByteArray()).
  const b = view.subarray(2, 18);
  const hex = (i: number) => b[i].toString(16).padStart(2, "0");
  const tunnelId =
    `${hex(3)}${hex(2)}${hex(1)}${hex(0)}-` +
    `${hex(5)}${hex(4)}-` +
    `${hex(7)}${hex(6)}-` +
    `${hex(8)}${hex(9)}-` +
    `${hex(10)}${hex(11)}${hex(12)}${hex(13)}${hex(14)}${hex(15)}`;

  // Payload : copie isolée (le buffer source peut être réutilisé par la WS).
  const payload = buf.slice(FRAME_HEADER_SIZE);
  return { tunnelId, payload };
}

/**
 * Installe `RdpTunnelSocket` à la place du constructeur global `WebSocket`,
 * le temps que le WASM ouvre SA connexion. La substitution est ciblée par
 * URL : seules les URLs commençant par `proxyMarker` sont interceptées ;
 * toute autre `new WebSocket(...)` retombe sur l'implémentation native — la
 * WS relais de la console n'est donc jamais perturbée.
 *
 * Retourne un objet de contrôle :
 *  - `waitForSocket` : promesse résolue avec l'instance interceptée (ou rejetée
 *    si le WASM n'ouvre rien dans le délai imparti) ;
 *  - `restore()` : restaure le `WebSocket` global natif. À appeler dès que
 *    le WASM a ouvert son socket (l'interception n'est plus nécessaire).
 */
export function installTunnelSocket(opts: {
  tunnelId: string;
  proxyMarker: string;
  relaySend: RelaySend;
  relaySendBinary: RelaySendBinary;
}): {
  proxyUrl: string;
  waitForSocket: Promise<RdpTunnelSocket>;
  restore: () => void;
} {
  const NativeWebSocket = globalThis.WebSocket;
  const proxyUrl = `${opts.proxyMarker}/${opts.tunnelId}`;
  let resolveSocket!: (s: RdpTunnelSocket) => void;
  let rejectSocket!: (e: Error) => void;
  let settled = false;

  const waitForSocket = new Promise<RdpTunnelSocket>((resolve, reject) => {
    resolveSocket = resolve;
    rejectSocket = reject;
  });

  // Proxy de constructeur : intercepte uniquement l'URL proxy de ce tunnel.
  const Patched = function (this: unknown, url: string | URL, protocols?: string | string[]) {
    const u = typeof url === "string" ? url : url.toString();
    if (u.startsWith(opts.proxyMarker)) {
      const sock = new RdpTunnelSocket(opts.tunnelId, opts.relaySend, opts.relaySendBinary);
      sock.url = u;
      if (!settled) {
        settled = true;
        resolveSocket(sock);
      }
      return sock;
    }
    // Toute autre WS (relais de la console, etc.) → implémentation native.
    return new NativeWebSocket(url, protocols);
  } as unknown as typeof WebSocket;

  // Recopie des constantes statiques pour rester compatible WebSocket.
  // `defineProperty` car `CONNECTING`/`OPEN`/… sont déclarées `readonly`.
  const statics: Record<string, number> = {
    CONNECTING: NativeWebSocket.CONNECTING,
    OPEN: NativeWebSocket.OPEN,
    CLOSING: NativeWebSocket.CLOSING,
    CLOSED: NativeWebSocket.CLOSED,
  };
  for (const [k, v] of Object.entries(statics)) {
    Object.defineProperty(Patched, k, { value: v, writable: false, enumerable: true });
  }

  globalThis.WebSocket = Patched;

  const restore = () => {
    globalThis.WebSocket = NativeWebSocket;
  };

  // Garde-fou : si le WASM n'ouvre jamais le socket, on ne reste pas bloqué.
  setTimeout(() => {
    if (!settled) {
      settled = true;
      restore();
      rejectSocket(new Error("IronRDP n'a pas ouvert de connexion (timeout)."));
    }
  }, 15000);

  return { proxyUrl, waitForSocket, restore };
}
